import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { CadsChipButtonConfig } from './cads-chip-button.config';
import { cadsChipButtonUsesLightBorder } from './cads-chip-button.util';

/**
 * Compact **hug-width** pill button for cards and contextual rows: custom fill and foreground colors, optional Material
 * Symbols icons, no border or shadow. Smaller than {@link CadsAppButtonComponent}.
 */
@Component({
    selector: 'cads-chip-button',
    imports: [NgStyle],
    templateUrl: './cads-chip-button.component.html',
    styleUrl: './cads-chip-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-chip-button-host',
    }
})
export class CadsChipButtonComponent {
  readonly config = input.required<CadsChipButtonConfig>();

  /** Emitted on click when not disabled. */
  readonly tapped = output<void>();

  protected readonly _surfaceStyle = computed(() => {
    const c = this.config();
    return {
      'background-color': c.backgroundColor ?? 'var(--cads-color-blue)',
      color: c.foregroundColor ?? 'var(--cads-color-white)',
    } as const;
  });

  protected readonly _leadingIcon = computed(() => this.config().leadingIcon?.trim() ?? '');
  protected readonly _trailingIcon = computed(() => this.config().trailingIcon?.trim() ?? '');

  /** Light fills (e.g. `var(--cads-color-white)`) get a `light-gray` border for contrast on white pages. */
  protected readonly _usesLightBorder = computed(() =>
    cadsChipButtonUsesLightBorder(this.config().backgroundColor)
  );

  protected _onClick(): void {
    if (this.config().disabled) {
      return;
    }
    this.tapped.emit();
  }
}
